<template>
  <DxPopup
    :visible="isVisible"
    :drag-enabled="true"
    :hide-on-outside-click="false"
    :show-close-button="false"
    :show-title="true"
    :title="popupTitle"
    width="95%"
    height="95%"
    :wrapper-attr="{ class: 'css-v2 gc-popup' }"
    @hidden="onPopupHidden"
    @shown="onPopupShown"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
    />

    <div
      id="timesheet-modal-wrapper"
      class="css-v2 gc-popup-form timesheet-modal-content"
    >
      <DxLoadPanel
        :visible.sync="isSaving"
        :show-indicator="true"
        :show-pane="true"
        :shading="true"
        :hide-on-outside-click="false"
        shading-color="rgba(0,0,0,0.3)"
      />
      <div class="view-card responsive-paddings timesheet-header-form">
        <div class="timesheet-header-row">
          <div class="timesheet-header-inputs">
            <gc-form
              :key="headerFormKey"
              :formOptions="headerFormOptions"
              :formItems="headerInputFormItems"
              :isNormal="true"
              :data-model="model"
              :formMode="localFormMode"
              :valueChangeCallBack="onHeaderValueChanged"
              ref="header-form"
            />
          </div>
          <div class="timesheet-header-summary">
            <gc-form
              :key="headerFormKey"
              :formOptions="headerFormOptions"
              :formItems="headerSummaryFormItems"
              :isNormal="true"
              :data-model="model"
              :formMode="localFormMode"
              ref="summary-form"
            />
          </div>
        </div>
      </div>

      <div class="timesheet-detail-grid">
        <dx-data-grid
          :data-source="detailLines"
          :ref="'detail-grid'"
          :height="'100%'"
          key-expr="_lineId"
          :column-chooser="{ enabled: false }"
          :repaint-changes-only="true"
          :two-way-binding-enabled="false"
          :focused-row-enabled="true"
          @cell-value-changed="onCellValueChanged"
          @editor-preparing="onEditorPreparing"
          @cell-prepared="onCellPrepared"
          @toolbar-preparing="onDetailToolbarPreparing"
          @editing-start="onDetailEditingStart"
          @focused-cell-changing="onDetailFocusedCellChanging"
          @focused-cell-changed="onDetailFocusedCellChanged"
          @key-down="onDetailGridKeyDown"
        >
          <DxKeyboardNavigation
            :edit-on-key-press="true"
            :enter-key-action="'startEdit'"
            :enter-key-direction="'none'"
            :enabled="true"
          />
          <DxEditing
            :allow-updating="!isReviewMode"
            :allow-deleting="false"
            :select-text-on-edit-start="true"
            mode="batch"
          />
          <DxScrolling
            mode="virtual"
            row-rendering-mode="virtual"
            :preload-enabled="true"
          />
          <DxPaging :enabled="false" />
          <DxPager :visible="false" />

          <DxColumn
            data-field="RequestCode"
            caption="Request"
            :width="180"
            alignment="left"
            :allow-editing="!isReviewMode"
            :css-class="
              !isReviewMode
                ? 'gc-editable-cell-color edit-cell-select-field'
                : ''
            "
            cell-template="requestCellTemplate"
            edit-cell-template="requestEditTemplate"
          />
          <DxColumn
            data-field="ProjectCode"
            caption="Project"
            :min-width="180"
            :width="320"
            alignment="left"
            :allow-editing="!isReviewMode"
            :css-class="
              !isReviewMode
                ? 'gc-editable-cell-color edit-cell-select-field'
                : ''
            "
            cell-template="projectCellTemplate"
            edit-cell-template="projectEditTemplate"
          />
          <DxColumn
            data-field="ProcessCode"
            caption="Process"
            :width="300"
            alignment="left"
            :allow-editing="!isReviewMode"
            :css-class="
              !isReviewMode
                ? 'gc-editable-cell-color edit-cell-select-field'
                : ''
            "
            cell-template="processCellTemplate"
            edit-cell-template="processEditTemplate"
          />
          <DxColumn
            data-field="TFSWorkItem"
            caption="Work Item"
            :width="100"
            alignment="left"
            :allow-editing="!isReviewMode"
            :css-class="!isReviewMode ? 'gc-editable-cell-color' : ''"
            edit-cell-template="tfsEditTemplate"
            :customize-text="(e) => (e.value > 0 ? String(e.value) : '')"
          />
          <DxColumn
            data-field="Description"
            caption="Description"
            :min-width="200"
            alignment="left"
            :allow-editing="!isReviewMode"
            :css-class="
              !isReviewMode ? 'gc-editable-cell-color comment-text' : ''
            "
            :word-wrap-enabled="true"
            cell-template="descriptionCellTemplate"
            edit-cell-template="descriptionEditTemplate"
          />
          <DxColumn
            data-field="Units"
            caption="Unit"
            :width="60"
            :allow-editing="!isReviewMode"
            :css-class="!isReviewMode ? 'gc-editable-cell-color' : ''"
            edit-cell-template="unitsEditTemplate"
          />
          <DxColumn
            data-field="HoursDisplay"
            caption="Hours"
            :width="60"
            :allow-editing="false"
            css-class="cell-disabled"
            :calculate-cell-value="calculateHoursDisplay"
            format="#0.00"
          />
          <DxColumn
            data-field="Billable"
            caption="Billable"
            :width="70"
            alignment="left"
            :allow-editing="!isReviewMode"
            cell-template="billableCellTemplate"
            edit-cell-template="billableEditTemplate"
            :show-editor-always="!isReviewMode"
          />
          <DxColumn
            v-if="!isReviewMode"
            type="buttons"
            :width="110"
            :fixed="true"
            fixed-position="right"
          >
            <DxButton
              icon="copy"
              hint="Copy Line"
              :on-click="(e) => copyLine(e.row.data)"
            />
            <DxButton
              icon="trash"
              hint="Delete"
              :on-click="(e) => deleteLine(e.row.data._lineId)"
            />
          </DxColumn>

          <template #requestCellTemplate="{ data }">
            <div class="custom-auto-complete">
              <span class="auto-complete-primary">{{
                data.data.RequestCode > 0 ? data.data.RequestCode : ""
              }}</span>
              <span
                v-if="data.data.RequestName"
                class="record-secondary gc-grid-code"
                >{{ data.data.RequestName }}</span
              >
            </div>
          </template>

          <template #requestEditTemplate="{ data: cellInfo }">
            <DxSelectBox
              :data-source="getRequestDataSource()"
              :value="cellInfo.value || null"
              display-expr="RequestCode"
              value-expr="RequestCode"
              styling-mode="underlined"
              :search-enabled="true"
              :search-expr="['RequestSearchText']"
              search-mode="contains"
              :show-clear-button="true"
              :item-template="'requestItemTpl'"
              @initialized="(e) => e.component.focus()"
              @value-changed="(e) => onRequestSelectChanged(e, cellInfo)"
              @focus-out="(e) => onRequestSelectFocusOut(e, cellInfo)"
              @key-down="(e) => onRequestCellKeyDown(e, cellInfo)"
              :search-timeout=10
            >
              <template #requestItemTpl="{ data: item }">
                <div class="custom-auto-complete">
                  <span class="auto-complete-primary"
                    >{{ item.RequestCode }} - {{ item.RequestName }}</span
                  >
                  <span class="record-secondary gc-grid-code">{{
                    item.CustomerName
                  }}</span>
                </div>
              </template>
            </DxSelectBox>
          </template>

          <template #projectCellTemplate="{ data }">
            <span
              >{{ data.data.ProjectCode
              }}<template v-if="data.data.ProjectDescription">
                — {{ data.data.ProjectDescription }}</template
              ></span
            >
          </template>

          <template #projectEditTemplate="{ data: cellInfo }">
            <DxSelectBox
              :data-source="getProjectDataSource()"
              :value="cellInfo.value || null"
              display-expr="ProjectCode"
              value-expr="ProjectCode"
              styling-mode="underlined"
              :search-enabled="!(cellInfo.data.RequestCode > 0)"
              :search-expr="['ProjectSearchText']"
              search-mode="contains"
              :show-clear-button="!(cellInfo.data.RequestCode > 0)"
              :read-only="cellInfo.data.RequestCode > 0"
              :item-template="'projectItemTpl'"
              @initialized="(e) => e.component.focus()"
              @value-changed="(e) => onProjectSelectChanged(e, cellInfo)"
              :search-timeout=10
            >
              <template #projectItemTpl="{ data: item }">
                <div class="custom-auto-complete">
                  <span class="auto-complete-primary">{{
                    item.ProjectCode
                  }}</span>
                  {{ " " }}
                  <span class="record-secondary gc-grid-code">{{
                    item.ProjectDescription
                  }}</span>
                </div>
              </template>
            </DxSelectBox>
          </template>

          <template #processCellTemplate="{ data }">
            <span
              >{{ data.data.ProcessCode
              }}<template v-if="data.data.ProcessDescription">
                — {{ data.data.ProcessDescription }}</template
              ></span
            >
          </template>

          <template #processEditTemplate="{ data: cellInfo }">
            <DxSelectBox
              :data-source="getProcessDataSource(cellInfo)"
              :value="cellInfo.value || null"
              display-expr="ProcessCode"
              value-expr="ProcessCode"
              styling-mode="underlined"
              :search-enabled="true"
              :search-expr="['ProcessSearchText']"
              search-mode="contains"
              :show-clear-button="true"
              :item-template="'processItemTpl'"
              @initialized="(e) => e.component.focus()"
              @value-changed="(e) => onProcessSelectChanged(e, cellInfo)"
              @focus-out="(e) => onProcessSelectFocusOut(e, cellInfo)"
              :search-timeout=10
            >
              <template #processItemTpl="{ data: item }">
                <div class="custom-auto-complete">
                  <span class="auto-complete-primary">{{
                    item.ProcessCode
                  }}</span>
                  {{ " " }}
                  <span class="record-secondary gc-grid-code">{{
                    item.ProcessDescription
                  }}</span>
                </div>
              </template>
            </DxSelectBox>
          </template>

          <template #tfsEditTemplate="{ data: cellInfo }">
            <DxTextBox
              :value="cellInfo.value > 0 ? String(cellInfo.value) : ''"
              :max-length="10"
              @initialized="(e) => e.component.focus()"
              @key-down="onTfsKeyDown"
              @value-changed="
                (e) => cellInfo.setValue(parseInt(e.value, 10) || 0)
              "
            />
          </template>

          <template #descriptionCellTemplate="{ data }">
            <div class="comment-cell-template">{{ data.data.Description }}</div>
          </template>

          <template #descriptionEditTemplate="{ data: cellInfo }">
            <DxTextArea
              :value="cellInfo.value"
              :max-length="1000"
              :height="30"
              :max-height="103"
              @initialized="
                (e) => {
                  e.component.focus();
                  onDescriptionHeightUpdate(e);
                }
              "
              @input="(e) => onDescriptionHeightUpdate(e)"
              @key-down="
                (e) => {
                  if (e.event.key === 'Enter') e.event.stopPropagation();
                }
              "
              @value-changed="(e) => cellInfo.setValue(e.value)"
            />
          </template>

          <template #unitsEditTemplate="{ data: cellInfo }">
            <DxNumberBox
              class="units-editor"
              :value="cellInfo.value"
              :min="isA01 ? null : 1"
              :max="9999"
              :show-spin-buttons="false"
              :format="'#0'"
              @initialized="(e) => e.component.focus()"
              @value-changed="(e) => onUnitValueChanged(e, cellInfo)"
            />
          </template>

          <template #billableCellTemplate="{ data }">
            <DxCheckBox :value="data.data.Billable === 'Y'" :disabled="true" />
          </template>

          <template #billableEditTemplate="{ data: cellInfo }">
            <DxCheckBox
              :value="cellInfo.data.Billable === 'Y'"
              :disabled="!cellInfo.data._billableEditable"
              @initialized="(e) => e.component.focus()"
              @value-changed="(e) => cellInfo.setValue(e.value ? 'Y' : 'N')"
            />
          </template>
        </dx-data-grid>
      </div>
    </div>

    <GcPopupAudit
      v-if="popupAuditVisible"
      :showPopup.sync="popupAuditVisible"
      :model.sync="auditModel"
    />

    <GcPopupValidationSummary
      v-if="formsConfig"
      :showPopup.sync="formsConfig.validationSummaryVisible"
      :items.sync="formsConfig.items"
      title="Validation Errors"
      :onHidden="onValidationSummaryHidden"
    />
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import {
  DxDataGrid,
  DxColumn,
  DxButton,
  DxEditing,
  DxScrolling,
  DxPaging,
  DxPager,
} from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxTextArea } from "devextreme-vue/text-area";
import { DxCheckBox } from "devextreme-vue/check-box";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import GcPopupAudit from "@/components/gc-popup-audit.vue";
import GcPopupValidationSummary from "@/components/gc-popup-validation-summary.vue";

import { GraycorpHelpdeskTimesheetService } from "@/_services/api/production/graycorp-helpdesk-timesheet.service";
import ApiService from "@/_services/api/api.service";
import appConfig from "@/app-config";
import { DateFromIsoWithoutZ } from "../../../_helpers/prototypes/prototype-date";
import FormMixin from "@/_mixins/form.mixin";
import { mapGetters } from "vuex";

ApiService.init(appConfig.VUE_APP_ROOT_API);

const MANDATORY_WORK_ITEM_PROCESSES = ["D24", "D26", "D28", "D39"];
const BIN_CUSTOMER_SERVICE = "997";
const BIN_INTERNAL = "998";
const BIN_DEVELOPMENT = "999";

let nextLineId = 1;

function combineDateAndTime(date, time) {
  if (!date || !time) return null;
  const d = new Date(date);
  const t = new Date(time);
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    t.getHours(),
    t.getMinutes(),
    t.getSeconds(),
  );
}

function newEmptyLine() {
  return {
    _lineId: nextLineId++,
    RequestCode: 0,
    RequestName: "",
    TaskCode: 0,
    ProjectCode: null,
    ProjectDescription: "",
    ProcessCode: null,
    ProcessDescription: "",
    TFSWorkItem: 0,
    Description: "",
    Billable: "Y",
    Units: 0,
    _binLocation: "",
    _isExceptionProcess: false,
    _billableEditable: false,
    _billableStatus: "",
    _actualHours: 0,
    _budgetHours: 0,
    _requestCommittedCode: 0,
  };
}

export default {
  mixins: [FormMixin],

  components: {
    DxPopup,
    DxDataGrid,
    DxColumn,
    DxButton,
    DxEditing,
    DxScrolling,
    DxPaging,
    DxPager,
    DxSelectBox,
    DxNumberBox,
    DxTextBox,
    DxTextArea,
    DxCheckBox,
    DxLoadPanel,
    GcPopupAudit,
    GcPopupValidationSummary,
  },

  props: {
    formMode: { type: String, default: "create" },
    timesheetCode: { type: Number, default: 0 },
    isVisible: { type: Boolean, default: false },
  },

  data() {
    return {
      localFormMode: "create",
      headerFormKey: 0,
      model: {
        TimesheetCode: 0,
        DateWorked: new Date(),
        LabourResource: null,
        LabourResourceName: "",
        LabourGroupCode: "",
        StartTime: new Date(1970, 0, 1, 0, 0, 0, 0),
        EndTime: new Date(1970, 0, 1, 0, 0, 0, 0),
        BreakMinutes: 0,
        WorkingHours: 0,
        HoursBooked: 0,
        HoursBilled: 0,
      },
      detailLines: [newEmptyLine()],
      isSaving: false,
      popupAuditVisible: false,
      auditModel: {
        createdBy: "",
        dateCreated: "",
        changedBy: "",
        dateChanged: "",
      },
    };
  },

  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),

    isReviewMode() {
      return this.localFormMode === "review";
    },

    isA01() {
      return this.model.LabourGroupCode === "A01";
    },

    popupTitle() {
      if (this.localFormMode === "create") return "Create Timesheet";
      if (!this.model.TimesheetCode) return "Timesheet";
      const name = this.model.LabourResourceName || "";
      const date = this.model.DateWorked
        ? new Date(this.model.DateWorked).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";
      return `Timesheet #${this.model.TimesheetCode} — ${name} ${date}`;
    },

    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: this.localFormMode === "review",
          options: {
            icon: "edit",
            onClick: this.onEnterEditMode,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: this.localFormMode !== "create",
          options: {
            icon: "clock",
            onClick: this.onAuditPopupHandler,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: this.localFormMode !== "review",
          options: {
            text: "Save",
            stylingMode: "contained",
            type: "default",
            onClick: this.onSave,
            disabled: this.isSaving,
            visible: this.localFormMode !== "review",
          },
        },
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: true,
          options: {
            icon: "close",
            onClick: this.onCancel,
            visible: true,
          },
        },
      ];
    },

    workingHours() {
      if (!this.model.StartTime || !this.model.EndTime) return 0;
      const start = new Date(this.model.StartTime);
      const end = new Date(this.model.EndTime);
      const mins = (end - start) / 60000 - (this.model.BreakMinutes || 0);
      return Math.max(0, mins / 60);
    },

    hoursBooked() {
      return this.detailLines.reduce((sum, l) => sum + (l.Units || 0), 0) / 4;
    },

    hoursBilled() {
      return (
        this.detailLines
          .filter((l) => l.Billable === "Y")
          .reduce((sum, l) => sum + (l.Units || 0), 0) / 4
      );
    },

    headerFormOptions() {
      return { gcLabelLocation: "top" };
    },

    headerInputFormItems() {
      const isReview = this.isReviewMode;
      return [
        {
          itemType: "group",
          colCount: 6,
          items: [
            {
              dataField: isReview ? "LabourResourceName" : "LabourResource",
              label: { text: "Team Member" },
              colSpan: 2,
              required: !isReview,
              editorType: isReview ? "dxTextBox" : "dxSelectBox",
              editorOptions: isReview
                ? { readOnly: true }
                : {
                    dataSource: this.getLabourResourceDataSource(),
                    displayExpr: (item) =>
                      item
                        ? `${item.Code} - ${item.FirstName} ${
                            item.Surname || ""
                          }`.trim()
                        : "",
                    valueExpr: "Code",
                    searchEnabled: true,
                    searchExpr: ["FirstName", "Surname", "Code"],
                    searchTimeout: 10,
                    showClearButton: true,
                    onValueChanged: (e) =>
                      this.onLabourResourceChanged(e.value),
                    itemTemplate: (data, _, container) => {
                      const wrap = document.createElement("div");
                      wrap.className = "custom-auto-complete";
                      const primary = document.createElement("span");
                      primary.className = "auto-complete-primary";
                      primary.textContent = data.Code || "";
                      wrap.appendChild(primary);
                      wrap.appendChild(document.createTextNode(" "));
                      const secondary = document.createElement("span");
                      secondary.className = "record-secondary gc-grid-code";
                      secondary.textContent = `${data.FirstName || ""} ${
                        data.Surname || ""
                      }`.trim();
                      wrap.appendChild(secondary);
                      container.appendChild(wrap);
                    },
                  },
            },
            {
              dataField: "DateWorked",
              label: { text: "Date" },
              colSpan: 1,
              required: !isReview,
              editorType: "dxDateBox",
              editorOptions: {
                displayFormat: "dd MMM yyyy",
                readOnly: isReview,
              },
            },
            {
              dataField: "StartTime",
              label: { text: "Start Time" },
              colSpan: 1,
              required: !isReview,
              editorType: "dxDateBox",
              editorOptions: {
                type: "time",
                displayFormat: "HH:mm",
                readOnly: isReview,
              },
            },
            {
              dataField: "EndTime",
              label: { text: "End Time" },
              colSpan: 1,
              required: !isReview,
              editorType: "dxDateBox",
              editorOptions: {
                type: "time",
                displayFormat: "HH:mm",
                readOnly: isReview,
              },
            },
            {
              dataField: "BreakMinutes",
              label: { text: "Break Time (mins)" },
              colSpan: 1,
              editorType: "dxNumberBox",
              editorOptions: {
                min: 0,
                showSpinButtons: false,
                readOnly: isReview,
                onKeyDown: isReview
                  ? undefined
                  : (e) => {
                      if (e.event.keyCode === 9 && !e.event.shiftKey) {
                        e.event.preventDefault();
                        setTimeout(() => {
                          this.focusFirstGridCell();
                        }, 1);
                      }
                    },
              },
            },
          ],
        },
      ];
    },

    headerSummaryFormItems() {
      return [
        {
          itemType: "group",
          colCount: 3,
          items: [
            {
              dataField: "WorkingHours",
              label: { text: "Hours Worked" },
              colSpan: 1,
              editorType: "dxNumberBox",
              editorOptions: {
                allowUpdate: false,
                readOnly: true,
                format: "#0.00",
              },
            },
            {
              dataField: "HoursBooked",
              label: { text: "Hours Booked" },
              colSpan: 1,
              editorType: "dxNumberBox",
              editorOptions: {
                allowUpdate: false,
                readOnly: true,
                format: "#0.00",
              },
            },
            {
              dataField: "HoursBilled",
              label: { text: "Hours Billed" },
              colSpan: 1,
              editorType: "dxNumberBox",
              editorOptions: {
                allowUpdate: false,
                readOnly: true,
                format: "#0.00",
              },
            },
          ],
        },
      ];
    },
  },

  created() {
    this._projectCache = {};
    this._processCache = {};
    this._requestCache = {};
    this._pendingFocusField = null;
    this._modalKeyHandler = (e) => {
      if (!this.isVisible) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        this.onCancel();
      }
    };
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this._modalKeyHandler, true);
  },

  watch: {
    isVisible(val) {
      if (val) {
        this._initModalReady = false;
        this._popupShown = false;
        document.addEventListener("keydown", this._modalKeyHandler, true);
        this.initModal().then(() => {
          this._initModalReady = true;
          this.tryApplyInitialFocus();
        });
      } else {
        this._initModalReady = false;
        this._popupShown = false;
        document.removeEventListener("keydown", this._modalKeyHandler, true);
      }
    },
    getHotKeySave(val) {
      if (val) {
        this.$store.dispatch("hotKey/setHotKeySave", false);
        if (!this.isReviewMode) this.onSave();
      }
    },

    workingHours(val) {
      this.model.WorkingHours = parseFloat(val.toFixed(2));
    },
    hoursBooked(val) {
      this.model.HoursBooked = parseFloat(val.toFixed(2));
    },
    hoursBilled(val) {
      this.model.HoursBilled = parseFloat(val.toFixed(2));
    },
  },

  methods: {
    async initModal() {
      nextLineId = 1;
      this._projectCache = {};
      this._processCache = {};
      this._requestCache = {};
      this._projectDataSource = null;
      this._processDataSourceByProject = {};
      this._requestDataSource = null;
      this.isSaving = false;
      this.localFormMode = this.formMode;

      if (this.timesheetCode > 0) {
        await this.loadTimesheet(this.timesheetCode);
      } else {
        this.model = {
          TimesheetCode: 0,
          DateWorked: new Date(),
          LabourResource: null,
          LabourResourceName: "",
          LabourGroupCode: "",
          StartTime: new Date(1970, 0, 1, 0, 0, 0, 0),
          EndTime: new Date(1970, 0, 1, 0, 0, 0, 0),
          BreakMinutes: 0,
          WorkingHours: 0,
          HoursBooked: 0,
          HoursBilled: 0,
        };
        this.detailLines = [newEmptyLine()];
      }

      this.headerFormKey += 1;
    },

    async loadTimesheet(code) {
      try {
        const [headerRes, detailRes] = await Promise.all([
          GraycorpHelpdeskTimesheetService.getHeader(code),
          GraycorpHelpdeskTimesheetService.getDetail(code),
        ]);

        const h = headerRes.data.value[0];
        if (!h) return;

        let labourGroupCode = "";
        let labourResourceName = h.LabourResourceName || "";
        try {
          const lrRes = await ApiService.oDataGet({
            url: "api/LabourResource",
            filter: `Code eq '${h.LabourResourceCode}'`,
            top: 1,
            select: "Code,FirstName,Surname,LabourGroupCode",
          });
          const lr = lrRes.data.value[0];
          if (lr) {
            labourGroupCode = lr.LabourGroupCode || "";
            labourResourceName = `${lr.Code || ""} - ${lr.FirstName} ${
              lr.Surname || ""
            }`.trim();
          }
        } catch (_) {
          /* ignore */
        }

        this.model = {
          TimesheetCode: h.TimesheetCode,
          DateWorked: h.DateWorked ? new Date(h.DateWorked) : new Date(),
          LabourResource: h.LabourResourceCode,
          LabourResourceName: labourResourceName,
          LabourGroupCode: labourGroupCode,
          StartTime: h.StartTime ? DateFromIsoWithoutZ(h.StartTime) : null,
          EndTime: h.EndTime ? DateFromIsoWithoutZ(h.EndTime) : null,
          BreakMinutes: h.BreakMinutes || 0,
          WorkingHours: 0,
          HoursBooked: 0,
          HoursBilled: 0,
        };

        this.auditModel = {
          createdBy: h.CreatedBy || "",
          dateCreated: h.DateCreated || "",
          changedBy: h.ChangedBy || "",
          dateChanged: h.DateChanged || "",
        };

        const lines = (detailRes.data.value || []).map((d) => ({
          _lineId: nextLineId++,
          RequestCode: d.RequestCode || 0,
          RequestName: d.RequestName || "",
          TaskCode: d.TaskCode || 0,
          ProjectCode: d.ProjectCode || null,
          ProjectDescription: d.ProjectDescription || "",
          ProcessCode: d.ProcessCode || null,
          ProcessDescription: d.ProcessDescription || "",
          TFSWorkItem: d.TFSWorkItem || 0,
          Description: d.Description || "",
          Billable: d.Billable || "Y",
          Units: d.Units || 0,
          _binLocation: d.BinLocation || "",
          _isExceptionProcess: d.IsExceptionProcess === "Y",
          _billableEditable:
            labourGroupCode === "A01" || d.IsExceptionProcess === "Y",
          _billableStatus: "",
          _actualHours: d.ActualHours || 0,
          _budgetHours: d.BudgetHours || 0,
          _requestCommittedCode: d.RequestCode || 0,
        }));

        this.detailLines = lines.length > 0 ? lines : [newEmptyLine()];
        if (!this.isReviewMode) {
          this.detailLines.push(newEmptyLine());
        }
      } catch (err) {
        this.$notify("Failed to load timesheet.", "error");
      }
    },

    onEnterEditMode() {
      this.localFormMode = "edit";
      this.headerFormKey += 1;
      if (this.detailLines.length > 0) {
        const last = this.detailLines[this.detailLines.length - 1];
        if (last.ProjectCode || last.ProcessCode || last.Units > 0) {
          this.detailLines.push(newEmptyLine());
        }
      }
      this.applyTeamMemberFocus();
    },

    async onLabourResourceChanged(code) {
      if (!code) {
        this.model.LabourGroupCode = "";
        this.model.LabourResourceName = "";
        return;
      }
      try {
        const res = await ApiService.oDataGet({
          url: "api/LabourResource",
          filter: `Code eq '${code}'`,
          top: 1,
          select: "Code,FirstName,Surname,LabourGroupCode",
        });
        const lr = res.data.value[0];
        if (lr) {
          this.model.LabourGroupCode = lr.LabourGroupCode || "";
          this.model.LabourResourceName = `${lr.FirstName} ${
            lr.Surname || ""
          }`.trim();
        }
      } catch (_) {
        /* ignore */
      }
    },

    onHeaderValueChanged() {},

    // ── Header ↔ Grid keyboard bridge ─────────────────────────────────────────

    focusFirstGridCell() {
      const grid = this.getDetailGrid();
      if (!grid) return;
      const cellEl = grid.getCellElement(0, 0);
      if (cellEl) {
        grid.focus(cellEl);
        grid.editCell(0, 0);
      }
    },

    focusBreakTimeField() {
      const formRef = this.$refs["header-form"];
      if (!formRef) return;
      const dxFormRef = formRef.$refs["gc-form-comp"];
      if (!dxFormRef || !dxFormRef.instance) return;
      const editor = dxFormRef.instance.getEditor("BreakMinutes");
      if (editor) editor.focus();
    },

    focusHeaderField(dataField) {
      const formRef = this.$refs["header-form"];
      if (!formRef) return;
      const dxFormRef = formRef.$refs["gc-form-comp"];
      if (!dxFormRef || !dxFormRef.instance) return;
      const editor = dxFormRef.instance.getEditor(dataField);
      if (editor) editor.focus();
    },

    onPopupShown() {
      this._popupShown = true;
      this.$store.dispatch("hotKey/setHotKeyPopupMode", true);
      this.tryApplyInitialFocus();
    },

    tryApplyInitialFocus() {
      if (!this._initModalReady || !this._popupShown) return;
      if (this.isReviewMode) return;
      this.applyTeamMemberFocus();
    },

    applyTeamMemberFocus() {
      this.$nextTick(() => {
        [0, 50, 150, 300, 500].forEach((d) =>
          setTimeout(() => this.focusHeaderField("LabourResource"), d),
        );
      });
    },

    onValidationSummaryHidden() {
      if (this._pendingFocusField) {
        this.focusHeaderField(this._pendingFocusField);
        this._pendingFocusField = null;
      }
    },

    onTfsKeyDown(e) {
      const { key, ctrlKey, metaKey } = e.event;
      const allowed = [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];
      if (!/^\d$/.test(key) && !ctrlKey && !metaKey && !allowed.includes(key)) {
        e.event.preventDefault();
      }
    },

    onRequestCellKeyDown(e, cellInfo) {
      if (
        e.event.keyCode === 9 &&
        e.event.shiftKey &&
        cellInfo.rowIndex === 0
      ) {
        e.component.blur();
        setTimeout(() => {
          e.event.preventDefault();
        }, 1);
        setTimeout(() => {
          this.focusBreakTimeField();
        }, 2);
      }
    },

    // ── Grid helpers ─────────────────────────────────────────────────────────

    calculateHoursDisplay(rowData) {
      return parseFloat(((rowData.Units || 0) / 4).toFixed(2));
    },

    getDetailGrid() {
      return this.$refs["detail-grid"]
        ? this.$refs["detail-grid"].instance
        : null;
    },

    updateLineField(lineId, field, value) {
      const line = this.detailLines.find((l) => l._lineId === lineId);
      if (line) {
        this.$set(line, field, value);
      }
    },

    // Batch-edit mode keeps its own pending-change store per cell, separate
    // from `detailLines`. Resetting a column's value through updateLineField
    // alone leaves a stale pending edit in place for that cell (from an
    // earlier, unrelated edit), so the grid keeps displaying the old value.
    // Use this for any column reset that isn't the field currently being
    // interactively edited (that one is already synced via cellInfo.setValue).
    resetLineField(lineId, dataField, value) {
      this.updateLineField(lineId, dataField, value);
      const grid = this.getDetailGrid();
      if (!grid) return;
      const rowIndex = grid.getRowIndexByKey(lineId);
      if (rowIndex >= 0) {
        grid.cellValue(rowIndex, dataField, value);
      }
    },

    // ── Data sources ─────────────────────────────────────────────────────────

    getLabourResourceDataSource() {
      return ApiService.createDataSource("api/LabourResource", {
        filter: [["Inactive", "=", "N"]],
        sort: [{ selector: "Code", asc: true }],
        select: ["Code", "FirstName", "Surname", "LabourGroupCode"],
      });
    },

    getProjectDataSource() {
      if (!this._projectDataSource) {
        this._projectDataSource = ApiService.createDataSource(
          "api/GraycorpHelpdeskTimesheetProjectSearch",
          {
            sort: [{ selector: "ProjectCode", desc: true }],
          },
        );
      }
      return this._projectDataSource;
    },

    getProcessDataSource(cellInfo) {
      const projectCode = cellInfo ? cellInfo.data.ProjectCode : null;
      if (!projectCode) return [];
      if (!this._processDataSourceByProject) {
        this._processDataSourceByProject = {};
      }
      if (!this._processDataSourceByProject[projectCode]) {
        this._processDataSourceByProject[projectCode] =
          ApiService.createDataSource(
            "api/GraycorpHelpdeskTimesheetProcessSearch",
            {
              filter: [["ProjectCode", "=", projectCode]],
              sort: [{ selector: "ProcessCode", asc: true }],
            },
          );
      }
      return this._processDataSourceByProject[projectCode];
    },

    getRequestDataSource() {
      if (!this._requestDataSource) {
        this._requestDataSource = ApiService.createDataSource(
          "api/GraycorpHelpdeskTimesheetJobSearch",
          {
            sort: [{ selector: "RequestCode", asc: false }],
          },
        );
      }
      return this._requestDataSource;
    },

    // ── Select change handlers ────────────────────────────────────────────────

    async onProjectSelectChanged(e, cellInfo) {
      const projectCode = e.value;
      cellInfo.setValue(projectCode);

      const lineId = cellInfo.data._lineId;
      this.updateLineField(lineId, "ProjectCode", projectCode);
      this.resetLineField(lineId, "RequestCode", 0);
      this.updateLineField(lineId, "RequestName", "");
      this.updateLineField(lineId, "_requestCommittedCode", 0);
      this.updateLineField(lineId, "TaskCode", 0);
      this.updateLineField(lineId, "ProcessDescription", "");
      this.resetLineField(lineId, "ProcessCode", null);
      this.resetLineField(lineId, "TFSWorkItem", 0);
      this.updateLineField(lineId, "_isExceptionProcess", false);
      this.updateLineField(lineId, "_billableStatus", "");

      if (!projectCode) {
        this.updateLineField(lineId, "_binLocation", "");
        this.updateLineField(lineId, "ProjectDescription", "");
        this.updateLineField(lineId, "_actualHours", 0);
        this.updateLineField(lineId, "_budgetHours", 0);
        this.resetLineField(lineId, "Billable", "Y");
        this.updateLineField(lineId, "_billableEditable", this.isA01);
        return;
      }

      const project = await this.fetchProject(projectCode);
      if (project) {
        this.updateLineField(
          lineId,
          "ProjectDescription",
          project.ProjectDescription || "",
        );
        this.updateLineField(lineId, "_binLocation", project.BinLocation || "");
        this.updateLineField(lineId, "_actualHours", project.ActualHours || 0);
        this.updateLineField(lineId, "_budgetHours", project.BudgetHours || 0);
        this.autoSetBillable(lineId);
      }

      const isLast =
        this.detailLines[this.detailLines.length - 1]._lineId === lineId;
      if (isLast) {
        this.detailLines.push(newEmptyLine());
      }
    },

    onRequestSelectChanged(e, cellInfo) {
      cellInfo.setValue(e.value);

      const isArrowNav =
        e.event &&
        e.event.type === "keydown" &&
        ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(
          e.event.key,
        );
      if (isArrowNav) return;

      this.commitRequestSelection(e.value, cellInfo, true);
    },

    onRequestSelectFocusOut(e, cellInfo) {
      const requestCode = e.component.option("value");
      const lineId = cellInfo.data._lineId;
      const line = this.detailLines.find((l) => l._lineId === lineId);
      if (line && line._requestCommittedCode === requestCode) return;

      this.commitRequestSelection(requestCode, cellInfo, true);
    },

    async commitRequestSelection(requestCode, cellInfo, moveFocus) {
      const lineId = cellInfo.data._lineId;
      this.updateLineField(lineId, "_requestCommittedCode", requestCode);
      this.updateLineField(lineId, "RequestCode", requestCode);

      if (!requestCode) {
        this.updateLineField(lineId, "RequestName", "");
        this.updateLineField(lineId, "_billableStatus", "");
        return;
      }

      const request = await this.fetchRequest(requestCode);
      if (request) {
        this.updateLineField(lineId, "RequestName", request.RequestName || "");
        this.updateLineField(
          lineId,
          "_billableStatus",
          request.BillableStatus || "",
        );

        if (request.ProjectCode) {
          this.updateLineField(
            lineId,
            "ProjectDescription",
            request.ProjectDescription || "",
          );
          this.resetLineField(lineId, "ProjectCode", request.ProjectCode);
          const project = await this.fetchProject(request.ProjectCode);
          if (project) {
            this.updateLineField(
              lineId,
              "_binLocation",
              project.BinLocation || "",
            );
            this.updateLineField(
              lineId,
              "_actualHours",
              project.ActualHours || 0,
            );
            this.updateLineField(
              lineId,
              "_budgetHours",
              project.BudgetHours || 0,
            );
          }
        }
        this.updateLineField(lineId, "ProcessDescription", "");
        this.resetLineField(lineId, "ProcessCode", null);
        this.resetLineField(lineId, "TFSWorkItem", 0);
        this.autoSetBillable(lineId);
      }

      const isLast =
        this.detailLines[this.detailLines.length - 1]._lineId === lineId;
      if (isLast) {
        this.detailLines.push(newEmptyLine());
      }

      if (moveFocus) this.focusGridCell(lineId, "ProcessCode");
    },

    focusGridCell(lineId, dataField) {
      this.$nextTick(() => {
        setTimeout(() => {
          const grid = this.getDetailGrid();
          if (!grid) return;
          const rowIndex = grid.getRowIndexByKey(lineId);
          if (rowIndex < 0) return;
          const cellEl = grid.getCellElement(rowIndex, dataField);
          if (cellEl) grid.focus(cellEl);
          grid.editCell(rowIndex, dataField);
        }, 100);
      });
    },

    onProcessSelectChanged(e, cellInfo) {
      cellInfo.setValue(e.value);
    },

    async onProcessSelectFocusOut(e, cellInfo) {
      const processCode = e.component.option("value");
      const lineId = cellInfo.data._lineId;

      if (!processCode) {
        this.updateLineField(lineId, "ProcessDescription", "");
        this.updateLineField(lineId, "_isExceptionProcess", false);
        this.autoSetBillable(lineId);
        return;
      }

      const line = this.detailLines.find((l) => l._lineId === lineId);
      const selectedItem = e.component.option("selectedItem");
      const process =
        selectedItem ||
        (await this.fetchProcess(line.ProjectCode, processCode));

      if (process && process.IsExceptionProcess === "Y") {
        const confirmed = await this.$confirm(
          "This is an exception process. Billable will be set to No. Do you want to continue?",
          "Exception Process",
        );
        if (!confirmed) {
          this.resetLineField(lineId, "ProcessCode", null);
          this.updateLineField(lineId, "ProcessDescription", "");
          this.updateLineField(lineId, "_isExceptionProcess", false);
          this.autoSetBillable(lineId);
          this.focusGridCell(lineId, "ProcessCode");
          return;
        }
        // resetLineField syncs both detailLines and the grid's batch-edit store,
        // preventing grid.refresh() from wiping the pending value.
        this.resetLineField(lineId, "ProcessCode", processCode);
        this.updateLineField(
          lineId,
          "ProcessDescription",
          process.ProcessDescription || "",
        );
        this.updateLineField(lineId, "_isExceptionProcess", true);
        this.resetLineField(lineId, "Billable", "N");
        this.updateLineField(lineId, "_billableEditable", true);

        this.$nextTick(() => {
          const grid = this.getDetailGrid();
          if (!grid) return;
          const rowIndex = grid.getRowIndexByKey(lineId);
          if (rowIndex !== -1) {
            grid.repaintRows([rowIndex]);
          }
        });
        this.focusGridCell(lineId, "TFSWorkItem");
      } else {
        if (process) {
          this.updateLineField(
            lineId,
            "ProcessDescription",
            process.ProcessDescription || "",
          );
          this.updateLineField(lineId, "_isExceptionProcess", false);
          this.updateLineField(
            lineId,
            "_actualHours",
            process.ActualHours || 0,
          );
          this.updateLineField(
            lineId,
            "_budgetHours",
            process.BudgetHours || 0,
          );
        }
        this.autoSetBillable(lineId);
      }
    },

    // ── Grid events ───────────────────────────────────────────────────────────

    onUnitValueChanged(e, cellInfo) {
      cellInfo.setValue(e.value);
      this.updateLineField(cellInfo.data._lineId, "Units", e.value);
    },

    onCellValueChanged(e) {
      const line = this.detailLines.find((l) => l._lineId === e.key);
      if (line && e.column.dataField === "Billable") {
        this.$set(line, "Billable", e.value);
      }
    },

    onEditorPreparing(e) {
      if (e.dataField === "Billable" && e.parentType === "dataRow") {
        const line = e.row && e.row.data;
        if (line && !line._billableEditable) {
          e.editorOptions.disabled = true;
        }
      }
    },

    onCellPrepared(e) {
      if (
        e.rowType === "data" &&
        (e.column.dataField === "Units" ||
          e.column.dataField === "HoursDisplay")
      ) {
        e.cellElement.style.textAlign = "right";
      }
      if (e.rowType === "data" && e.column.dataField === "Billable") {
        if (e.data && !e.data._billableEditable) {
          e.cellElement.classList.add("cell-disabled");
        }
      }
      if (e.rowType === "data" && e.column.dataField === "ProjectCode") {
        if (e.data && e.data.RequestCode > 0) {
          e.cellElement.classList.add("cell-disabled");
        }
      }
    },

    isCellSkippable(column, rowData) {
      if (!column || column.type === "buttons") return true;
      if (column.allowEditing === false) return true;
      if (column.dataField === "Billable") {
        return !rowData || !rowData._billableEditable;
      }
      if (column.dataField === "ProjectCode") {
        return !!rowData && rowData.RequestCode > 0;
      }
      return false;
    },

    onDetailFocusedCellChanging(e) {
      e.isHighlighted = true;
      if (this.isReviewMode) return;

      const goingForward =
        e.newRowIndex > e.prevRowIndex ||
        (e.newRowIndex === e.prevRowIndex &&
          e.newColumnIndex > e.prevColumnIndex);
      const step = goingForward ? 1 : -1;
      const columnCount = e.columns.length;
      const rowCount = this.detailLines.length;

      let rowIndex = e.newRowIndex;
      let colIndex = e.newColumnIndex;

      for (let guard = 0; guard < columnCount * 2; guard++) {
        if (colIndex < 0) {
          rowIndex -= 1;
          colIndex = columnCount - 1;
        } else if (colIndex >= columnCount) {
          rowIndex += 1;
          colIndex = 0;
        }
        if (rowIndex < 0 || rowIndex >= rowCount) return;

        const rowData = this.detailLines[rowIndex];
        if (!this.isCellSkippable(e.columns[colIndex], rowData)) break;
        colIndex += step;
      }

      if (rowIndex !== e.newRowIndex || colIndex !== e.newColumnIndex) {
        e.newRowIndex = rowIndex;
        e.newColumnIndex = colIndex;
      }
    },

    onDetailFocusedCellChanged(e) {
      e.isHighlighted = true;
    },

    onDetailGridKeyDown(e) {
      const key = e.event && e.event.key;
      if (!key) return;
      const target = e.event.target;
      const inEditor =
        target && target.closest && target.closest(".dx-editor-cell");
      if (!inEditor) return;
      if (key === "ArrowDown" || key === "ArrowUp" || key.length === 1) {
        e.handled = true;
      }
    },

    onDetailToolbarPreparing(e) {
      e.toolbarOptions.items = [];
    },

    onDetailEditingStart(e) {
      if (
        e.column &&
        e.column.dataField === "ProjectCode" &&
        e.data &&
        e.data.RequestCode > 0
      ) {
        e.cancel = true;
      }
    },

    onDescriptionHeightUpdate(e) {
      const component = e.component;
      const value =
        (e.event && e.event.target && e.event.target.value) ||
        component.option("value") ||
        "";
      const lineCount = (value.match(/\n/g) || []).length;
      if (lineCount < 7) {
        const newHeight = lineCount > 2 ? lineCount * 16 : 30;
        component.beginUpdate();
        component.option("height", newHeight);
        component.endUpdate();
        const el = component.element();
        if (el) {
          const row = el.closest("tr");
          if (row) row.style.height = newHeight + "px";
        }
        const grid = this.getDetailGrid();
        if (grid) grid.updateDimensions();
      }
    },

    isLastDetailRow(e) {
      return (
        this.detailLines.length > 0 &&
        e.row.data._lineId ===
          this.detailLines[this.detailLines.length - 1]._lineId
      );
    },

    addLineAfter(rowData) {
      const idx = this.detailLines.findIndex(
        (l) => l._lineId === rowData._lineId,
      );
      this.detailLines.splice(idx + 1, 0, newEmptyLine());
    },

    copyLine(rowData) {
      const copy = Object.assign({}, rowData, { _lineId: nextLineId++ });
      const idx = this.detailLines.findIndex(
        (l) => l._lineId === rowData._lineId,
      );
      this.detailLines.splice(idx + 1, 0, copy);
      this.$nextTick(() => {
        setTimeout(() => {
          const grid = this.getDetailGrid();
          if (!grid) return;
          const rowIndex = grid.getRowIndexByKey(copy._lineId);
          if (rowIndex < 0) return;
          const cellEl = grid.getCellElement(rowIndex, "ProjectCode");
          if (cellEl) grid.focus(cellEl);
          grid.editCell(rowIndex, "ProjectCode");
        }, 100);
      });
    },

    async deleteLine(lineId) {
      const confirmed = await this.$confirm(
        "Are you sure you want to delete this record?",
        "Delete",
      );
      if (!confirmed) return;
      const idx = this.detailLines.findIndex((l) => l._lineId === lineId);
      if (idx !== -1) this.detailLines.splice(idx, 1);
      if (this.detailLines.length === 0) this.detailLines.push(newEmptyLine());
      const targetIdx = Math.min(idx, this.detailLines.length - 1);
      const targetLine = this.detailLines[targetIdx];
      this.$nextTick(() => {
        setTimeout(() => {
          const grid = this.getDetailGrid();
          if (!grid) return;
          const rowIndex = grid.getRowIndexByKey(targetLine._lineId);
          if (rowIndex < 0) return;
          const cellEl = grid.getCellElement(rowIndex, "ProjectCode");
          if (cellEl) grid.focus(cellEl);
          grid.editCell(rowIndex, "ProjectCode");
        }, 100);
      });
    },

    // ── Billable auto-set ─────────────────────────────────────────────────────

    autoSetBillable(lineId) {
      const line = this.detailLines.find((l) => l._lineId === lineId);
      if (!line || line._isExceptionProcess) return;

      const bin = line._binLocation;
      let billable = "Y";
      const editable = this.isA01;

      if (bin === BIN_INTERNAL) {
        billable = "N";
      } else if (bin === BIN_CUSTOMER_SERVICE) {
        billable = line._billableStatus === "Non-Billable" ? "N" : "Y";
      } else if (bin === BIN_DEVELOPMENT) {
        billable = line._actualHours < line._budgetHours ? "Y" : "N";
      }

      this.updateLineField(lineId, "_billableEditable", editable);
      this.resetLineField(lineId, "Billable", billable);
    },

    // ── Data fetch helpers with cache ─────────────────────────────────────────

    async fetchProject(projectCode) {
      if (this._projectCache[projectCode])
        return this._projectCache[projectCode];
      try {
        const res = await ApiService.oDataGet({
          url: "api/GraycorpHelpdeskTimesheetProjectSearch",
          filter: `ProjectCode eq ${projectCode}`,
          top: 1,
        });
        const item = res.data.value[0];
        if (item) this._projectCache[projectCode] = item;
        return item || null;
      } catch (_) {
        return null;
      }
    },

    async fetchProcess(projectCode, processCode) {
      const key = `${projectCode}:${processCode}`;
      if (this._processCache[key]) return this._processCache[key];
      try {
        const res = await ApiService.oDataGet({
          url: "api/GraycorpHelpdeskTimesheetProcessSearch",
          filter: `ProjectCode eq ${projectCode} and ProcessCode eq '${processCode}'`,
          top: 1,
        });
        const item = res.data.value[0];
        if (item) this._processCache[key] = item;
        return item || null;
      } catch (_) {
        return null;
      }
    },

    async fetchRequest(requestCode) {
      if (this._requestCache[requestCode])
        return this._requestCache[requestCode];
      try {
        const res = await ApiService.oDataGet({
          url: "api/GraycorpHelpdeskTimesheetJobSearch",
          filter: `RequestCode eq ${requestCode}`,
          top: 1,
        });
        const item = res.data.value[0];
        if (item) this._requestCache[requestCode] = item;
        return item || null;
      } catch (_) {
        return null;
      }
    },

    // ── Save ──────────────────────────────────────────────────────────────────

    async onSave() {
      const grid = this.getDetailGrid();
      if (grid) grid.saveEditData();

      if (!this.validateBeforeSave()) return;

      this.isSaving = true;
      try {
        const payload = this.buildSavePayload();
        const res = await GraycorpHelpdeskTimesheetService.save(payload);
        this.$notify("Timesheet saved successfully.", "success");
        this.$emit("saved", res.data.TimesheetCode);
        this.$emit("update:isVisible", false);
      } catch (err) {
        const msg =
          (err.data && err.data) ||
          "An error occurred while saving the timesheet.";
        this.$notify(
          typeof msg === "string" ? msg : JSON.stringify(msg),
          "error",
        );
      } finally {
        this.isSaving = false;
      }
    },

    validateBeforeSave() {
      const m = this.model;
      const errors = [];
      let firstFailField = null;

      if (!m.LabourResource) {
        errors.push("Team Member is required.");
        if (!firstFailField) firstFailField = "LabourResource";
      }
      if (!m.DateWorked) {
        errors.push("Date Worked is required.");
        if (!firstFailField) firstFailField = "DateWorked";
      }
      if (!m.StartTime) {
        errors.push("Start Time is required.");
        if (!firstFailField) firstFailField = "StartTime";
      }
      if (!m.EndTime) {
        errors.push("End Time is required.");
        if (!firstFailField) firstFailField = "EndTime";
      } else if (new Date(m.EndTime) <= new Date(m.StartTime)) {
        errors.push("End Time must be after Start Time.");
        if (!firstFailField) firstFailField = "EndTime";
      }

      if (!this.isA01) {
        const filledLines = this.detailLines.filter(
          (l) => l.ProjectCode || l.ProcessCode || l.Description || l.Units > 0,
        );

        if (this.workingHours <= 0) {
          errors.push("Working Hours must be greater than zero.");
        }

        if (filledLines.length === 0) {
          errors.push("At least one detail line is required.");
        } else {
          if (this.hoursBooked < this.workingHours) {
            errors.push(
              "Hours Booked must be at least equal to Working Hours.",
            );
          }

          for (const line of filledLines) {
            if (!line.ProjectCode) {
              errors.push("Project is required on all detail lines.");
              break;
            }
            if (!line.ProcessCode) {
              errors.push("Process is required on all detail lines.");
              break;
            }
            if (!line.Description) {
              errors.push("Description is required on all detail lines.");
              break;
            }
            if ((line.Units || 0) <= 0) {
              errors.push(
                "Units must be greater than zero on all detail lines.",
              );
              break;
            }
            if (line._binLocation === BIN_CUSTOMER_SERVICE) {
              if (!line.RequestCode || line.RequestCode <= 0) {
                errors.push(
                  "Request is required for Customer Service (997) projects.",
                );
                break;
              }
            }
            if (
              MANDATORY_WORK_ITEM_PROCESSES.includes(
                (line.ProcessCode || "").toUpperCase(),
              )
            ) {
              if (!line.TFSWorkItem || line.TFSWorkItem <= 0) {
                errors.push(
                  `Work Item is required for Process ${line.ProcessCode}.`,
                );
                break;
              }
            }
          }
        }
      }

      if (errors.length > 0) {
        this._pendingFocusField = firstFailField || null;
        this.displayValidationErrors(errors);
        return false;
      }

      return true;
    },

    buildSavePayload() {
      const m = this.model;
      const dateWorked = new Date(m.DateWorked);

      const startDateTime = combineDateAndTime(dateWorked, m.StartTime);
      const endDateTime = combineDateAndTime(dateWorked, m.EndTime);

      const filledLines = this.detailLines.filter(
        (l) => l.ProjectCode && l.ProcessCode,
      );

      return {
        TimesheetCode: m.TimesheetCode || 0,
        DateWorked: dateWorked.toISOString(),
        LabourResource: m.LabourResource,
        StartTime: startDateTime ? startDateTime.toUTCDateTimeString() : null,
        EndTime: endDateTime ? endDateTime.toUTCDateTimeString() : null,
        BreakMinutes: m.BreakMinutes || 0,
        TimesheetType: "Working Hours",
        TimesheetDtls: filledLines.map((l) => ({
          WorkOrder: l.ProjectCode,
          ProcessCode: l.ProcessCode,
          TimeWorked: (l.Units || 0) * 15,
          Description: l.Description || "",
          Billable: l.Billable || "Y",
          RequestCode: l.RequestCode || 0,
          TaskCode: l.TaskCode || 1,
          TFSWorkItem: l.TFSWorkItem || 0,
        })),
      };
    },

    onAuditPopupHandler() {
      this.popupAuditVisible = !this.popupAuditVisible;
    },

    // ── Cancel / close ────────────────────────────────────────────────────────

    async onCancel() {
      if (!this.isReviewMode) {
        const confirmed = await this.$confirm(
          "You have unsaved changes. Are you sure you want to leave?",
          "Unsaved Changes",
        );
        if (!confirmed) return;
      }
      this.$emit("update:isVisible", false);
    },

    onPopupHidden() {
      this.model = {
        TimesheetCode: 0,
        DateWorked: new Date(),
        LabourResource: null,
        LabourResourceName: "",
        LabourGroupCode: "",
        StartTime: null,
        EndTime: null,
        BreakMinutes: 0,
        WorkingHours: 0,
        HoursBooked: 0,
        HoursBilled: 0,
      };
      this.detailLines = [newEmptyLine()];
      this.localFormMode = "create";
      this.headerFormKey += 1;
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("update:isVisible", false);
    },
  },
};
</script>

<style scoped>
.timesheet-modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.timesheet-header-form {
  flex-shrink: 0;
  margin-bottom: 8px;
}

.timesheet-header-row {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.timesheet-header-inputs {
  flex: 1;
  min-width: 0;
}

.timesheet-header-summary {
  flex-shrink: 0;
  border-left: 1px solid var(--gc-border-color, #e0e0e0);
  padding-left: 16px;
  margin-left: 16px;
}

.timesheet-detail-grid {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

::v-deep .timesheet-detail-grid .dx-datagrid-header-panel {
  display: none;
}

::v-deep .timesheet-detail-grid .comment-cell-template {
  white-space: pre-wrap;
  word-break: break-word;
}

::v-deep
  .timesheet-detail-grid
  .dx-editor-cell
  .dx-texteditor
  .dx-texteditor-input,
::v-deep
  .timesheet-detail-grid
  .dx-editor-cell
  .dx-texteditor
  .dx-placeholder::before {
  padding-left: 0 !important;
}

::v-deep .units-editor .dx-texteditor-input {
  text-align: right !important;
}

::v-deep .timesheet-detail-grid .dx-button {
  background-color: unset !important;
}

::v-deep .timesheet-detail-grid .dx-button .dx-button-content .dx-icon::before {
  color: #344054 !important;
}

::v-deep .dx-form {
  height: 85px !important;
}

.dx-datagrid .dx-datagrid-content .dx-datagrid-table .dx-row .dx-editor-cell {
  padding-left: 5px !important;
  padding-right: 5px !important;
}
</style>

<style lang="scss">
.timesheet-modal-content {
  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: top !important;
    padding-top: 3px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-header-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: middle !important;
    padding-top: 10px !important;
  }
}
</style>
